// frontend/src/api/apiFetch.js
//
// fetch() with the same auth behaviour axios already has.
//
// App.js installs axios interceptors that attach the bearer token and log the
// user out on 401. Raw fetch() calls get neither — so a request made with a
// token the server no longer accepts fails silently, and any caller that
// retries keeps retrying with the same dead token. That is exactly how a
// rotated JWT_SECRET turned into a poller hammering /wave/check-status every
// four seconds, indefinitely.
//
// Use this instead of fetch() for any call to our API.

const isPublicPage = () => {
  const path = window.location.pathname;
  return ['/login', '/signup'].includes(path)
    || path.startsWith('/verify-email')
    || path.startsWith('/reset-password')
    || path.startsWith('/workshops/');
};

/**
 * @throws {Error} with `status` set, on any non-2xx response.
 * @returns {Promise<any>} the parsed JSON body
 */
export async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('authToken');

  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    // Same rule as the axios interceptor: drop the dead token and send the
    // user to sign in, unless they are already somewhere that does not need
    // one. Without this the caller sees a normal response object and carries
    // on as though nothing happened.
    if (!isPublicPage()) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    const err = new Error('Session expirée');
    err.status = 401;
    throw err;
  }

  // Callers routinely did `await res.json()` without checking res.ok, so an
  // error body was read as if it were data. Failing loudly is the point.
  if (!res.ok) {
    let message = `La requête a échoué (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error || body?.message) message = body.error || body.message;
    } catch {
      /* not JSON — keep the status message */
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  if (res.status === 204) return null;
  return res.json();
}

export default apiFetch;
